import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AiAgentFlowService } from 'src/app/private/services/ai-agent-flow.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

const DEFAULT_STAGE = () => ({
  name: '',
  goal: '',
  llm_advance_hint: '',
  collect: [],
  required_before_advance: [],
  max_turns: 3,
  fallback_message: '',
  is_terminal: false,
  interruptible: false,
});

const DEFAULT_FAQ = () => ({
  question: '',
  answer: '',
});

export const VOICE_STYLES = ['professional', 'friendly', 'formal', 'casual', 'empathetic'];
export const BOOKING_BACKENDS = ['none', 'calendly', 'google_calendar'];

@Component({
  selector: 'app-create-ai-agent-flow',
  templateUrl: './create-ai-agent-flow.component.html',
  styleUrls: ['./create-ai-agent-flow.component.scss'],
})
export class CreateAiAgentFlowComponent implements OnInit, OnChanges {

  @Input()  flowData: any = null;   // null = create mode
  @Output() saved     = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  stages: any[] = [];
  faqs: any[] = [];
  collectFieldInput = '';
  btnLoading = false;
  activeTab: 'basic' | 'stages' | 'faqs' | 'settings' | 'preview' = 'basic';
  isSubmitted = false;

  voiceStyles    = VOICE_STYLES;
  bookingBackends = BOOKING_BACKENDS;

  // Editing indices
  editingStageIndex: number | null = null;
  editingFaqIndex: number | null = null;

  get isEditMode() { return !!this.flowData?.id; }
  get jsonPreview() {
    try { return JSON.stringify(this.buildPayload(), null, 2); }
    catch { return '{}'; }
  }

  constructor(
    private fb: FormBuilder,
    private service: AiAgentFlowService,
    private ls: LocalStorageService,
    private toast: ToastrService,
  ) {}

  ngOnInit() { this.initForm(); }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['flowData']) {
      this.initForm();
    }
  }

  initForm() {
    const d = this.flowData || {};
    this.form = this.fb.group({
      flow_name:           [d.flow_name        || '', Validators.required],
      persona_name:        [d.persona_name     || ''],
      persona_company:     [d.persona_company  || ''],
      objective:           [d.objective        || ''],
      voice_style:         [d.voice_style      || 'professional'],
      opening_message:     [d.opening_message  || ''],
      max_retries_unclear: [d.max_retries_unclear ?? 3, [Validators.min(1), Validators.max(10)]],
      collect_fields_raw:  [''],   // temp input field
      booking_enabled:     [d.booking_enabled  ?? false],
      booking_backend:     [d.booking_backend  || 'none'],
      booking_trigger_stages_raw: [(d.booking_trigger_stages || []).join(', ')],
      calendly_api_token:  [d.calendly_api_token        || ''],
      calendly_event_type_uri: [d.calendly_event_type_uri || ''],
      calendly_timezone:   [d.calendly_timezone         || 'Asia/Kolkata'],
    });

    this.stages = d.stages ? JSON.parse(JSON.stringify(d.stages)) : [];
    this.faqs   = d.faqs   ? JSON.parse(JSON.stringify(d.faqs))   : [];
    this.activeTab = 'basic';
    this.editingStageIndex = null;
    this.editingFaqIndex   = null;
    this.isSubmitted = false;
  }

  // ── Collect Fields helpers ──────────────────────────────────────────────────
  get collectFields(): string[] {
    const d = this.flowData || {};
    // Read from the live form object we're building
    return this._collectFields;
  }
  _collectFields: string[] = [];

  ngAfterViewInit() {
    this._collectFields = this.flowData?.collect_fields ? [...this.flowData.collect_fields] : [];
  }

  addCollectField() {
    const v = this.form.get('collect_fields_raw')?.value?.trim();
    if (v && !this._collectFields.includes(v)) {
      this._collectFields.push(v);
      this.form.get('collect_fields_raw')?.setValue('');
    }
  }
  removeCollectField(f: string) {
    this._collectFields = this._collectFields.filter((x) => x !== f);
  }

  // ── Stage helpers ───────────────────────────────────────────────────────────
  addStage() {
    this.stages.push(DEFAULT_STAGE());
    this.editingStageIndex = this.stages.length - 1;
  }

  removeStage(i: number) {
    this.stages.splice(i, 1);
    if (this.editingStageIndex === i) this.editingStageIndex = null;
  }

  moveStage(i: number, dir: 1 | -1) {
    const j = i + dir;
    if (j < 0 || j >= this.stages.length) return;
    [this.stages[i], this.stages[j]] = [this.stages[j], this.stages[i]];
  }

  toggleStageCollect(stage: any, field: string) {
    if (!stage.collect) stage.collect = [];
    const idx = stage.collect.indexOf(field);
    idx === -1 ? stage.collect.push(field) : stage.collect.splice(idx, 1);
  }

  toggleStageRequired(stage: any, field: string) {
    if (!stage.required_before_advance) stage.required_before_advance = [];
    const idx = stage.required_before_advance.indexOf(field);
    idx === -1 ? stage.required_before_advance.push(field) : stage.required_before_advance.splice(idx, 1);
  }

  // ── FAQ helpers ─────────────────────────────────────────────────────────────
  addFaq() {
    this.faqs.push(DEFAULT_FAQ());
    this.editingFaqIndex = this.faqs.length - 1;
  }

  removeFaq(i: number) {
    this.faqs.splice(i, 1);
    if (this.editingFaqIndex === i) this.editingFaqIndex = null;
  }

  moveFaq(i: number, dir: 1 | -1) {
    const j = i + dir;
    if (j < 0 || j >= this.faqs.length) return;
    [this.faqs[i], this.faqs[j]] = [this.faqs[j], this.faqs[i]];
  }

  // ── Build payload ───────────────────────────────────────────────────────────
  buildPayload() {
    const f = this.form.value;
    const bookingTriggerRaw = f.booking_trigger_stages_raw || '';
    const bookingTriggerStages = bookingTriggerRaw
      .split(',').map((s: string) => s.trim()).filter(Boolean);

    return {
      client_id:               this.ls.getClientId(),
      flow_name:               f.flow_name,
      persona_name:            f.persona_name,
      persona_company:         f.persona_company,
      objective:               f.objective,
      voice_style:             f.voice_style,
      opening_message:         f.opening_message,
      max_retries_unclear:     f.max_retries_unclear,
      collect_fields:          this._collectFields,
      booking_enabled:         f.booking_enabled,
      booking_backend:         f.booking_backend,
      booking_trigger_stages:  bookingTriggerStages,
      calendly_api_token:      f.booking_backend === 'calendly' ? f.calendly_api_token : null,
      calendly_event_type_uri: f.booking_backend === 'calendly' ? f.calendly_event_type_uri : null,
      calendly_timezone:       f.calendly_timezone,
      stages:                  this.stages,
      faqs:                    this.faqs.filter(faq => faq.question?.trim() || faq.answer?.trim()),
      created_by:              this.ls.getUserId(),
    };
  }

  // ── Submit ──────────────────────────────────────────────────────────────────
  submit() {
    this.isSubmitted = true;
    if (this.form.invalid) {
      this.activeTab = 'basic';
      return;
    }

    this.btnLoading = true;
    const payload = this.buildPayload();

    const call = this.isEditMode
      ? this.service.update({ ...payload, id: this.flowData.id, updated_by: this.ls.getUserId() })
      : this.service.create(payload);

    call.subscribe({
      next: () => {
        this.btnLoading = false;
        this.saved.emit();
      },
      error: (err: any) => {
        this.btnLoading = false;
        this.toast.error(err?.error?.message || 'Failed to save flow');
      },
    });
  }
}
